import { AccountModalNames, useAccountModals } from '@/components/Modals/Account';
import { useBoardsContext } from '@/features/Boards/context-provider';
import { DialogCreateBoard } from '@/features/Boards/dialog-create-board';
import { CreateBoardDrawer } from '@/features/Boards/drawer-create-board';
import { BoardsAuthRequired } from '@/features/Boards/partials/AuthRequired';
import { BoardsPageContainer } from '@/features/Boards/partials/BoardPageContainer';
import { BoardPageEmpty } from '@/features/Boards/partials/BoardPageEmpty';
import { BoardsPageSort } from '@/features/Boards/partials/BoardsPageSort';
import { BoardTile, BoardTileLoading } from '@/features/Boards/tile';
import { useIsLoggedIn } from '@/hooks/auth';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import type { GetBoardsItemResponse } from '@/open-api/model';
import { Plus } from '@music-vine/cadence/icons';
import { Alpha, Bravo, Button, type ButtonProps, Separator, Text, toast } from '@music-vine/cadence/ui';
import { AnimatePresence, motion } from 'motion/react';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
const CreateBoardButton = (props: ButtonProps) => <Button className="gap-1 pl-3 font-semibold text-sm md:text-xs lg:gap-2 lg:text-sm" noFeedback variant="bold" {...props} data-sentry-element="Button" data-sentry-component="CreateBoardButton" data-sentry-source-file="index.page.tsx">
    <Plus className="size-4 lg:size-5" data-sentry-element="Plus" data-sentry-source-file="index.page.tsx" /> Create board
  </Button>;
const BoardsPageHeader = () => {
  const {
    boards
  } = useBoardsContext();
  const {
    isMd
  } = useTWScreens();
  const userIsLoggedIn = useIsLoggedIn();
  return <div className="flex w-full flex-wrap xxs:items-center xxs:justify-between gap-x-8 gap-y-4" data-sentry-component="BoardsPageHeader" data-sentry-source-file="index.page.tsx">
      <Alpha className="whitespace-nowrap" data-sentry-element="Alpha" data-sentry-source-file="index.page.tsx">My boards</Alpha>
      <div className="flex w-full xs:w-min items-center justify-between gap-4">
        {isMd ? <DialogCreateBoard mode="create_board_only">
            <CreateBoardButton disabled={!userIsLoggedIn} />
          </DialogCreateBoard> : <CreateBoardDrawer mode="create_board_only">
            <CreateBoardButton disabled={!userIsLoggedIn} />
          </CreateBoardDrawer>}
        <BoardsPageSort disabled={!boards.length} data-sentry-element="BoardsPageSort" data-sentry-source-file="index.page.tsx" />
      </div>
      <Separator className="mt-4" data-sentry-element="Separator" data-sentry-source-file="index.page.tsx" />
    </div>;
};
const BoardsGridLoading = () => <div className={cn('grid grid-flow-row grid-cols-1 gap-4 py-4 md:grid-cols-2 lg:py-8 xl:grid-cols-3 2xl:grid-cols-4')} data-sentry-component="BoardsGridLoading" data-sentry-source-file="index.page.tsx">
    {Array.from([1, 2, 3, 4, 5, 6, 7, 8]).map(item => <BoardTileLoading key={item} />)}
  </div>;
const BoardsGrid = ({
  boards,
  favoritesBoardId
}: {
  boards: GetBoardsItemResponse[];
  favoritesBoardId: number | null;
}) => {
  const {
    boardsQueryResponse: {
      fetchNextPage: fetchNextPageBoards,
      hasNextPage
    }
  } = useBoardsContext();
  const observerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        fetchNextPageBoards();
      }
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 0
    });
    const node = observerRef.current;
    if (node) {
      observer.observe(node);
    }
    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [fetchNextPageBoards]);
  return <div className={cn('grid grid-flow-row grid-cols-1 gap-4 py-4 md:grid-cols-2 lg:py-8 xl:grid-cols-3 2xl:grid-cols-4')} data-sentry-component="BoardsGrid" data-sentry-source-file="index.page.tsx">
      <AnimatePresence initial={false} mode="popLayout" data-sentry-element="AnimatePresence" data-sentry-source-file="index.page.tsx">
        {boards.map((board, boardIndex) => {
        if (!board) {
          return null;
        }
        const {
          name,
          isPinned,
          id,
          assetImageUrls
        } = board;
        const isFavoritesBoard = id === favoritesBoardId;
        return <motion.div animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.98
        }} initial={{
          opacity: 0,
          scale: 1
        }} key={board.id} layout transition={{
          duration: 0.4,
          ease: 'easeInOut'
        }}>
              <BoardTile assetImageUrls={assetImageUrls} boardIndex={boardIndex} id={board.id} isFavoritesBoard={isFavoritesBoard} isPinned={isPinned} key={board.id} name={name} />
            </motion.div>;
      })}
      </AnimatePresence>

      {!!hasNextPage && <BoardTileLoading ref={observerRef} />}
    </div>;
};
export default function MyBoardsPage() {
  const {
    boards,
    favoritesBoardId,
    boardsQueryResponse: {
      refetch: refetchBoards,
      isFetching: isFetchingBoards
    }
  } = useBoardsContext();
  const userIsLoggedIn = useIsLoggedIn();
  const router = useRouter();
  const {
    source
  } = router.query;
  const isLegacyPlaylistRedirect = source === 'legacy-playlist';
  const {
    setCurrentModal
  } = useAccountModals();

  // Track if component has mounted to prevent showing soft 404 during SSR/initial hydration
  // This prevents logged-in users from briefly seeing the logged-out view before auth loads
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Show toast for logged-in users coming from legacy playlist links
  useEffect(() => {
    if (userIsLoggedIn && isLegacyPlaylistRedirect) {
      toast.info({
        title: 'Playlists are now Boards',
        description: 'Your playlists have been moved to Boards',
        duration: 6000
      });

      // Clean up the URL by removing the source parameter
      const {
        source: _,
        ...restQuery
      } = router.query;
      router.replace({
        pathname: router.pathname,
        query: restQuery
      }, undefined, {
        shallow: true
      });
    }
  }, [userIsLoggedIn, isLegacyPlaylistRedirect, router.query, router.pathname, router.replace]);
  useEffect(() => {
    if (!userIsLoggedIn) {
      return;
    }
    refetchBoards();
  }, [refetchBoards, userIsLoggedIn]);

  // Show soft 404 for logged-out users redirected from legacy playlist links
  // Only show after mount to prevent flash for logged-in users whose auth hasn't loaded yet
  if (hasMounted && !userIsLoggedIn && isLegacyPlaylistRedirect) {
    return <>
        <Head>
          <meta content="noindex" name="robots" />
          <title>My Boards • Uppbeat</title>
        </Head>
        <BoardsPageContainer>
          <BoardsAuthRequired message={<div className="flex flex-col items-center gap-4 text-center">
                <Bravo>Playlists are now Boards</Bravo>
                <Text>
                  This link is no longer active.
                  <br />
                  Log in to access your boards, or ask the owner to resend the
                  new link.
                </Text>
                <div className="flex gap-2">
                  <Button onClick={() => setCurrentModal(AccountModalNames.Login)} size="lg" variant="brand">
                    Log in
                  </Button>
                  <Button asChild size="lg" variant="light">
                    <Link href="/">Back to homepage</Link>
                  </Button>
                </div>
              </div>} />
        </BoardsPageContainer>
      </>;
  }

  // Regular logged-out view (not from legacy redirect)
  if (hasMounted && !userIsLoggedIn) {
    return <>
        <Head>
          <meta content="noindex" name="robots" />
          <title>My Boards • Uppbeat</title>
        </Head>
        <BoardsPageContainer className="items-center pb-0">
          <BoardsPageHeader />
          <BoardsAuthRequired />
        </BoardsPageContainer>
      </>;
  }
  if (isFetchingBoards && !boards?.length) {
    return <>
        <Head>
          <meta content="noindex" name="robots" />
          <title>My Boards • Uppbeat</title>
        </Head>
        <BoardsPageContainer className="items-center pb-0">
          <BoardsPageHeader />
          <BoardsGridLoading />
        </BoardsPageContainer>
      </>;
  }
  if (!boards?.length) {
    return <>
        <Head>
          <meta content="noindex" name="robots" />
          <title>My Boards • Uppbeat</title>
        </Head>
        <BoardsPageContainer className="items-center pb-0">
          <BoardsPageHeader />
          <BoardPageEmpty title="No boards here yet" />
        </BoardsPageContainer>
      </>;
  }
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="index.page.tsx">
        <meta content="noindex" name="robots" data-sentry-element="meta" data-sentry-source-file="index.page.tsx" />
        <title>My Boards • Uppbeat</title>
      </Head>
      <BoardsPageContainer data-sentry-element="BoardsPageContainer" data-sentry-source-file="index.page.tsx">
        <BoardsPageHeader data-sentry-element="BoardsPageHeader" data-sentry-source-file="index.page.tsx" />
        <BoardsGrid boards={boards} favoritesBoardId={favoritesBoardId} data-sentry-element="BoardsGrid" data-sentry-source-file="index.page.tsx" />
      </BoardsPageContainer>
    </>;
}